import { isLibraryPathname } from "@/lib/library/library-routes";
import { ACCOUNT_PATH } from "@/lib/account/account-routes";
import { isRecitalsPathname, RECITALS_BASE_PATH } from "@/lib/live/recital-routes";
import { studioViewFromPathname } from "@/lib/studio/studio-routes";
import type { StudioView } from "@/lib/studio/studio";

// Le modèle de navigation vit à part parce qu'il est lu depuis deux surfaces :
// la nav en ligne de la barre et le sheet du palier compact. Le dupliquer, ce
// serait accepter qu'un jour une entrée existe d'un côté et pas de l'autre.
//
// Il a deux niveaux, et pas cinq entrées à plat, parce que l'application en a
// deux : Harmonie et Accords sont des vues du Studio, elles n'existent que par
// lui et ne remontent même pas le piano-roll quand on bascule de l'une à
// l'autre (voir `studio-routes.ts`). Bibliothèque et Apprendre, elles, sont des
// destinations. Mettre les cinq au même rang, c'était dire à l'écran l'inverse
// de ce que fait le code.

export type AppPage =
  | "library"
  | "recitals"
  | "studio"
  | "harmony"
  | "chords"
  | "lab"
  | "learn"
  /** Hors de la nav : aucun onglet ne s'allume, le fil d'Ariane le nomme. */
  | "account";

export type NavItem = {
  key: AppPage;
  label: string;
  /** Destination route. `null` for entries that route through `view`. */
  href: string | null;
  /** Studio sub-view to activate on click. Each view owns a route
   *  (`/studio`, `/studio/harmony`, …), so selecting one navigates. */
  view: StudioView | null;
};

/** Une entrée de premier niveau. `children` n'est peuplé que pour le Studio,
 *  mais le champ est total : une destination sans sous-vue porte un tableau
 *  vide, jamais `undefined`, pour que les deux surfaces bouclent dessus sans
 *  se demander à chaque fois si le niveau existe. */
export type NavNode = NavItem & {
  children: readonly NavItem[];
};

// `Accords` et non `Chords` : c'était le seul mot anglais d'une barre
// francophone, et le seul qu'un débutant ne pouvait pas deviner. Il coûte 5 px.
const STUDIO_VIEWS: readonly NavItem[] = [
  { key: "harmony", label: "Harmonie", href: null, view: "harmony" },
  { key: "chords", label: "Accords", href: null, view: "chords" },
  // Le banc d'essai des niveaux, en développement seulement.
  //
  // Il est ici et pas seulement dans l'URL parce que taper une route du studio
  // est une navigation *dure* : la page se remonte et le morceau chargé est
  // perdu, puisqu'il vit en mémoire et que rien ne le recharge. Une vue
  // qu'aucune surface n'atteint côté client est donc une vue qu'on ne peut
  // ouvrir qu'à vide, ce qui la rend inutilisable pour ce qu'elle sert à
  // mesurer.
  //
  // `development` et non « pas production » : sous test, `NODE_ENV` vaut
  // `test`, et la suite qui garde la forme de cette nav doit continuer à
  // affirmer la nav du produit, pas celle de l'établi. `NODE_ENV` est
  // statiquement remplacé au build, donc l'entrée disparaît de la production
  // plutôt que d'y être masquée.
  ...(process.env.NODE_ENV === "development"
    ? [
        { key: "lab" as const, label: "Labo", href: null, view: "lab" as const },
      ]
    : []),
];

export const NAV_TREE: readonly NavNode[] = [
  {
    key: "library",
    label: "Bibliothèque",
    href: "/library",
    view: null,
    children: [],
  },
  {
    key: "studio",
    label: "Studio",
    href: null,
    view: "roll",
    children: STUDIO_VIEWS,
  },
  { key: "learn", label: "Apprendre", href: "/learn", view: null, children: [] },
  // En queue, à droite : décision de l'utilisateur (2026-09-13). Une première
  // version l'intercalait entre la Bibliothèque et le Studio au nom de l'ordre
  // du geste ; à l'usage, la barre se lit comme les trois piliers du produit
  // puis ce qui s'y ajoute, et le direct est ce qui s'y ajoute.
  //
  // Pas d'enfants : « Mes récitals » et « Mes enregistrements » sont des pages
  // de `/recitals`, pas des vues. Le second niveau de cette barre ne sert
  // qu'aux instruments du Studio, qui ne sont pas des destinations — et la
  // carte de gauche de la section porte déjà ses quatre lignes.
  {
    key: "recitals",
    // « Récital » et non « Live » : c'est ce qu'on y fait, et c'est le mot que
    // le reste du produit emploie — la salle d'attente, le panneau, le replay.
    // « Live » nommait la technique ; le menu nomme la destination. L'adresse
    // a fini par suivre le 2026-09-19 : `/live` annonçait une chose et en
    // ouvrait trois, dont ce qu'on a gardé.
    label: "Récital",
    href: RECITALS_BASE_PATH,
    view: null,
    children: [],
  },
];

/** Les deux surfaces qui lisent ce modèle. Elles n'ont pas le même budget de
 *  largeur, et c'est la seule raison pour laquelle elles peuvent diverger. */
export type NavSurface = "inline" | "sheet";

// Un parcours tourne dans le Studio sans quitter `/studio` :
// rien n'annonçait qu'on pouvait en sortir, alors que tout clic de
// nav en sort bel et bien (`handleNavClick` relâche l'engagement). La sortie
// est donc une ligne à elle, et non plus le renommage de l'entrée « Studio » :
// une action et une destination ne sont pas la même chose, et depuis que le
// Studio a des enfants, les lui accrocher sous une étiquette « Quitter
// l'exercice » ne voulait plus rien dire.
//
// Seul le sheet la reçoit. La nav en ligne fait 425 px et les zones latérales
// tombent à leur contenu naturel (134 px) : le plancher du header est à 729 px,
// soit 39 px de marge sous les 768 px d'apparition de la nav en ligne. « Quitter
// l'exercice » en réclame 63, on est donc toujours court, et la sortie explicite
// reste sur cette surface celle de la toolbar (bascule « Apprendre / Libre »),
// toujours à l'écran. Les lignes du sheet, elles, sont pleine largeur.
const LEARN_EXIT: NavItem = {
  key: "studio",
  label: "Quitter l'exercice",
  href: null,
  view: "roll",
};

/** Ce qu'une surface a à afficher : l'arbre, plus la ligne de sortie du
 *  parcours quand elle a la place de la porter. */
export type NavModel = {
  exit: NavItem | null;
  nodes: readonly NavNode[];
};

// Constantes figées : l'identité des entrées ne bouge pas d'un rendu à l'autre.
const DEFAULT_MODEL: NavModel = { exit: null, nodes: NAV_TREE };
const LEARN_SHEET_MODEL: NavModel = { exit: LEARN_EXIT, nodes: NAV_TREE };

/**
 * Le modèle de nav pour une surface, selon qu'un parcours est en cours.
 *
 * Un booléen et non le parcours lui-même : la nav n'a pas à savoir lequel des
 * deux tourne, seulement qu'il y a quelque chose à quitter. Les deux reçoivent
 * la même ligne de sortie, et c'est ce qu'il faut : la question posée est
 * « puis-je revenir au studio », pas « par quel chemin j'apprenais ».
 */
export function getNavModel(engaged: boolean, surface: NavSurface): NavModel {
  if (engaged && surface === "sheet") {
    return LEARN_SHEET_MODEL;
  }

  return DEFAULT_MODEL;
}

/**
 * Où l'on se trouve, en deux informations et non plus une seule.
 *
 * `page` seule ne suffisait pas : sur `/studio/harmony` elle vaut « harmony »,
 * et l'entrée « Studio » retombait au gris des entrées au repos — éteinte au
 * moment précis où l'on travaille dedans. Elle n'était allumée que sur une
 * route sur cinq. `studioActive` est le troisième état qui manquait : « vous
 * êtes dedans », distinct de « vous êtes ici ».
 */
export type NavState = {
  /** L'entrée qui porte la page courante. */
  page: AppPage;
  /** Le Studio est-il sur le chemin de la page courante ? Vrai aussi quand
   *  `page` vaut « studio », les deux surfaces font la différence. */
  studioActive: boolean;
};

// `libraryOpen` a disparu de la signature : la bibliothèque était le seul état
// de ce modèle qui ne vivait pas dans l'URL, et c'est ce qui obligeait le
// header à s'abonner au contexte du Studio pour savoir où l'on se trouve. Elle
// a maintenant ses routes, comme tout le reste.
export function getNavState(pathname: string, engaged: boolean): NavState {
  if (isLibraryPathname(pathname)) {
    return { page: "library", studioActive: false };
  }

  // Avant le test d'engagement, comme la bibliothèque : on peut très bien
  // regarder qui joue pendant qu'un parcours est en cours dans le studio resté
  // monté derrière. C'est l'URL qui dit où l'on est quand on en a quitté la
  // surface.
  if (isRecitalsPathname(pathname)) {
    return { page: "recitals", studioActive: false };
  }

  // « Mon compte » se rend par-dessus le Studio resté monté : sans cette
  // ligne, il tombait dans le `switch` et allumait « Studio ».
  if (pathname === ACCOUNT_PATH || pathname.startsWith(`${ACCOUNT_PATH}/`)) {
    return { page: "account", studioActive: false };
  }

  // L'engagement compte autant que l'URL : pendant un parcours on est sur
  // `/studio`, mais la page courante est « Apprendre ». Le Studio n'est pas
  // marqué actif pour autant, le parcours occupe la surface, et c'est la
  // bascule de la toolbar qui rend la main, pas la nav.
  //
  // Les deux parcours répondent pareil. Le parcours harmonique a eu sa propre
  // route, `/studio/level`, et tombait donc dans le `switch` ci-dessous : il
  // allumait « Studio » pendant qu'on travaillait un niveau, là où le parcours
  // par cœur allumait « Apprendre ». Un seul axe, une seule réponse.
  if (engaged || pathname.startsWith("/learn")) {
    return { page: "learn", studioActive: false };
  }

  // On the studio routes, the active entry follows the view segment so
  // Harmonie/Accords light up inside the group. Reading the pathname
  // (rather than the observable it feeds) keeps the highlight correct on
  // the very first paint of a deep-linked view.
  // See spec §08 invariant 5.
  switch (studioViewFromPathname(pathname)) {
    case "harmony":
      return { page: "harmony", studioActive: true };

    case "chords":
      return { page: "chords", studioActive: true };

    case "lab":
      return { page: "lab", studioActive: true };

    default:
      return { page: "studio", studioActive: true };
  }
}

/** Libellé affiché pour une page, `null` si l'entrée n'existe plus. Les
 *  sous-vues comptent : c'est ce libellé que la barre compacte affiche à côté
 *  de la marque, et « Harmonie » y est une réponse aussi valable que
 *  « Bibliothèque ». */
export function navLabel(page: AppPage): string | null {
  if (page === "account") {
    return "Mon compte";
  }

  for (const node of NAV_TREE) {
    if (node.key === page) {
      return node.label;
    }

    const child = node.children.find((item) => item.key === page);

    if (child !== undefined) {
      return child.label;
    }
  }

  return null;
}
